// Import auth utilities
import { checkResponseStatus } from '../utils/authUtils';

// Helper function to get token - avoids circular dependency
const getAuthToken = () => {
  // Try to get from localStorage first (fallback)
  return localStorage.getItem('access_token') || sessionStorage.getItem('access_token');
};

export async function fetchProjects(page = 1, limit = 15) {
  const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
  if (!API_BASE_URL) {
    throw new Error('REACT_APP_API_BASE_URL environment variable is not set');
  }

  // Get token using helper function
  const accessToken = getAuthToken();

  const headers = {
    'Content-Type': 'application/json',
  };

  // Add authorization header and cookie with JWT token
  if (accessToken) {
    headers['Authorization'] = `Bearer ${accessToken}`;
    headers['Cookie'] = `jwt=${accessToken}`;
  }

  console.log('Making fetch projects request to:', `${API_BASE_URL}api/v1/dashboard/project/?page=${page}&limit=${limit}`);

  const response = await fetch(`${API_BASE_URL}api/v1/dashboard/project/?page=${page}&limit=${limit}`, {
    method: 'GET',
    headers,
  });

  // Check for 401 status
  checkResponseStatus(response);

  if (!response.ok) {
    let errorMessage = 'Failed to fetch projects';
    try {
      const errorData = await response.json();
      errorMessage = errorData.message || errorMessage;
    } catch (e) {
      // If response is not JSON (like HTML error page), get the text
      const errorText = await response.text();
      console.error('Non-JSON response:', errorText);
      errorMessage = `Server error: ${response.status} ${response.statusText}`;
    }
    throw new Error(errorMessage);
  }

  return response.json();
}

export async function createProject(projectData) {
  const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
  if (!API_BASE_URL) {
    throw new Error('REACT_APP_API_BASE_URL environment variable is not set');
  }

  // Get token using helper function
  const accessToken = getAuthToken();

  const headers = {
    'Content-Type': 'application/json',
  };

  // Add authorization header and cookie with JWT token
  if (accessToken) {
    headers['Authorization'] = `Bearer ${accessToken}`;
    headers['Cookie'] = `jwt=${accessToken}`;
  }

  console.log('Making create project request to:', `${API_BASE_URL}api/v1/dashboard/project/create`);

  const response = await fetch(`${API_BASE_URL}api/v1/dashboard/project/create`, {
    method: 'POST',
    headers,
    body: JSON.stringify(projectData),
  });

  // Check for 401 status
  checkResponseStatus(response);

  if (!response.ok) {
    let errorMessage = 'Failed to create project';
    try {
      const errorData = await response.json();
      errorMessage = errorData.message || errorMessage;
    } catch (e) {
      // If response is not JSON (like HTML error page), get the text
      const errorText = await response.text();
      console.error('Non-JSON response:', errorText);
      errorMessage = `Server error: ${response.status} ${response.statusText}`;
    }
    throw new Error(errorMessage);
  }

  return response.json();
}

export async function deleteProject(projectId) {
  const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
  if (!API_BASE_URL) {
    throw new Error('REACT_APP_API_BASE_URL environment variable is not set');
  }

  // Get token using helper function
  const accessToken = getAuthToken();

  const headers = {
    'Content-Type': 'application/json',
  };

  // Add authorization header and cookie with JWT token
  if (accessToken) {
    headers['Authorization'] = `Bearer ${accessToken}`;
    headers['Cookie'] = `jwt=${accessToken}`;
  }

  console.log('Making delete project request to:', `${API_BASE_URL}api/v1/dashboard/project/delete/${projectId}`);

  const response = await fetch(`${API_BASE_URL}api/v1/dashboard/project/delete/${projectId}`, {
    method: 'DELETE',
    headers,
  });

  // Check for 401 status
  checkResponseStatus(response);

  if (!response.ok) {
    let errorMessage = 'Failed to delete project';

    // Clone the response before reading it
    const responseClone = response.clone();

    try {
      const errorData = await response.json();
      errorMessage = errorData.message || errorMessage;

      // Handle 401 authentication errors specifically
      if (response.status === 401) {
        console.error('Authentication failed. Token might be expired or invalid.');
        // Clear invalid tokens
        localStorage.removeItem('access_token');
        localStorage.removeItem('refresh_token');
        sessionStorage.removeItem('access_token');
        sessionStorage.removeItem('refresh_token');
        errorMessage = 'Authentication failed. Please login again.';
      }
    } catch (e) {
      // If response is not JSON, try reading as text from the cloned response
      try {
        const errorText = await responseClone.text();
        console.error('Non-JSON response:', errorText);
        errorMessage = `Server error: ${response.status} ${response.statusText}`;
      } catch (textError) {
        console.error('Failed to read response as text:', textError);
        errorMessage = `Server error: ${response.status} ${response.statusText}`;
      }
    }
    throw new Error(errorMessage);
  }

  return response.json();
}

export async function updateProject(projectId, projectData) {
  const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
  if (!API_BASE_URL) {
    throw new Error('REACT_APP_API_BASE_URL environment variable is not set');
  }

  // Get token using helper function
  const accessToken = getAuthToken();

  const headers = {
    'Content-Type': 'application/json',
  };

  // Add authorization header and cookie with JWT token
  if (accessToken) {
    headers['Authorization'] = `Bearer ${accessToken}`;
    headers['Cookie'] = `jwt=${accessToken}`;
  }

  console.log('Making update project request to:', `${API_BASE_URL}api/v1/dashboard/project/update/${projectId}`);

  const response = await fetch(`${API_BASE_URL}api/v1/dashboard/project/update/${projectId}`, {
    method: 'PATCH',
    headers,
    body: JSON.stringify(projectData),
  });

  // Check for 401 status
  checkResponseStatus(response);

  if (!response.ok) {
    let errorMessage = 'Failed to update project';
    try {
      const errorData = await response.json();
      errorMessage = errorData.message || errorMessage;
    } catch (e) {
      // If response is not JSON (like HTML error page), get the text
      const errorText = await response.text();
      console.error('Non-JSON response:', errorText);
      errorMessage = `Server error: ${response.status} ${response.statusText}`;
    }
    throw new Error(errorMessage);
  }

  return response.json();
}

export async function searchProjects(name) {
  const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
  if (!API_BASE_URL) {
    throw new Error('REACT_APP_API_BASE_URL environment variable is not set');
  }

  // Get token using helper function
  const accessToken = getAuthToken();

  const headers = {
    'Content-Type': 'application/json',
  };

  // Add authorization header and cookie with JWT token
  if (accessToken) {
    headers['Authorization'] = `Bearer ${accessToken}`;
    headers['Cookie'] = `jwt=${accessToken}`;
  }
  console.log('Making search projects request to:', `${API_BASE_URL}api/v1/dashboard/project/search`);

  const response = await fetch(`${API_BASE_URL}api/v1/dashboard/project/search`, {
    method: 'POST',
    headers,
    body: JSON.stringify({ "name": name }),
  });

  // Check for 401 status
  checkResponseStatus(response);

  if (!response.ok) {
    let errorMessage = 'Failed to search projects';
    try {
      const errorText = await response.text();
      console.error('Search projects error response:', errorText);
      errorMessage = errorText || errorMessage;
    } catch (textError) {
      console.error('Failed to read error response as text:', textError);
      errorMessage = `Server error: ${response.status} ${response.statusText}`;
    }
    throw new Error(errorMessage);
  }

  const data = await response.json();
  console.log('Search projects response:', data);
  return data;
}

export async function fetchProjectQuarterInsight(projectId) {
  const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
  if (!API_BASE_URL) {
    throw new Error('REACT_APP_API_BASE_URL environment variable is not set');
  }

  const accessToken = getAuthToken();

  const headers = {
    'Content-Type': 'application/json',
  };

  if (accessToken) {
    headers['Authorization'] = `Bearer ${accessToken}`;
    headers['Cookie'] = `jwt=${accessToken}`;
  }

  const url = `${API_BASE_URL}api/v1/dashboard/insight/project-quarter-insight?project_id=${projectId}`;
  console.log('Making fetch project quarter insight request to:', url);

  const response = await fetch(url, {
    method: 'GET',
    headers,
  });

  await checkResponseStatus(response);

  if (!response.ok) {
    let errorMessage = 'Failed to fetch project quarter insight';
    try {
      const errorText = await response.text();
      console.error('Project quarter insight error response:', errorText);
      errorMessage = errorText || errorMessage;
    } catch (textError) {
      console.error('Failed to read error response as text:', textError);
      errorMessage = `Server error: ${response.status} ${response.statusText}`;
    }
    throw new Error(errorMessage);
  }

  const data = await response.json();
  console.log('Project quarter insight response:', data);
  return data;
}